import { z } from 'zod/v3';

// ========== Risk Register DTOs ==========

export const RiskTypeEnum = z.enum(['OPERATIONAL', 'FRAUD', 'VULNERABILITY']);
export const RiskRatingEnum = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export const RiskLikelihoodEnum = z.enum(['VERY_LOW', 'LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH']);
export const RiskImpactEnum = z.enum(['VERY_LOW', 'LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH']);
export const RiskStatusEnum = z.enum(['OPEN', 'ASSESSING', 'PENDING_REVIEW', 'IN_PROGRESS', 'CLOSED', 'MONITORING']);
export const RiskTreatmentEnum = z.enum(['MITIGATE', 'ACCEPT', 'TRANSFER', 'AVOID']);
export const RiskSourceTypeEnum = z.enum([
  'ZAP', 'GITHUB_SCAN', 'SNYK', 'PENTEST', 'MANUAL',
  'DEPENDENCY_SCAN', 'CONFIG_REVIEW', 'SCAN_FINDING', 'GAP_ANALYSIS',
  'POSTURE_ATTESTATION', 'OTHER',
]);
export const RiskSyncStatusEnum = z.enum(['SYNCED', 'STALE', 'ARCHIVED']);
// #996: derived vulnerability SLA state (never persisted — computed at read time).
export const SlaStateEnum = z.enum(['RESOLVED', 'BREACHED', 'DUE_SOON', 'WITHIN_SLA', 'NONE']);
export const RiskSortFieldEnum = z.enum(['displayId', 'riskRating', 'category', 'owner']);
export const RiskSortDirectionEnum = z.enum(['asc', 'desc']);
export const RiskAttentionEnum = z.enum(['OVERDUE', 'REVIEW_DUE']);
export type RiskAttentionFilter = z.infer<typeof RiskAttentionEnum>;

export const RiskAcceptanceDTOSchema = z.object({
  id: z.string(), justification: z.string(), approverId: z.string().nullable(),
  approverName: z.string().nullable(), approverEmail: z.string().nullable(),
  requestedById: z.string().nullable(), requestedAt: z.coerce.date(),
  questionnaireException: z.boolean().optional(),
  approvedAt: z.coerce.date().nullable(), expiresAt: z.coerce.date(),
  status: z.enum(['PENDING', 'ACTIVE', 'EXPIRED', 'REVOKED']),
  revokedAt: z.coerce.date().nullable(), revokedReason: z.string().nullable(),
});
export type RiskAcceptanceDTO = z.infer<typeof RiskAcceptanceDTOSchema>;

export const PolicyExceptionRequestDTOSchema = z.object({
  riskId: z.string(), displayId: z.string(), rationale: z.string(),
  status: z.enum(['PENDING', 'APPROVED', 'EXPIRED', 'REVOKED']),
  policyText: z.string().nullable(),
});
export type PolicyExceptionRequestDTO = z.infer<typeof PolicyExceptionRequestDTOSchema>;

export const RiskItemDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  riskType: RiskTypeEnum,
  sequenceNumber: z.number(),
  displayId: z.string(), // e.g. "RSK-001", "FRD-002", "VUL-003"
  title: z.string(),
  riskStatement: z.string(),
  affectedSystem: z.string().nullable(),
  category: z.string(),
  sourceType: RiskSourceTypeEnum,
  likelihood: RiskLikelihoodEnum,
  impact: RiskImpactEnum,
  riskRating: RiskRatingEnum,
  scoringVersion: z.union([z.literal(1), z.literal(2)]).optional(),
  riskScore: z.number().int().min(0).max(100),
  mitigationPlan: z.string().nullable(),
  ownerId: z.string().nullable(),
  ownerLabel: z.string(),
  ownerRoleName: z.string().nullable(), // from ComplianceRole join
  targetDate: z.coerce.date().nullable(),
  reviewDate: z.coerce.date().nullable(),
  status: RiskStatusEnum,
  aiAssessmentRunning: z.boolean().optional(),
  aiAssessmentState: z.enum(['queued', 'running', 'none', 'unknown']).optional(),
  hasCompletedAssessment: z.boolean().optional(),
  lastAssessedAt: z.coerce.date().nullable().optional(), // Latest completed human assessment; optional for historical snapshots.
  hasConfirmedAssessment: z.boolean().optional(),
  invalidatedAt: z.coerce.date().nullish(),
  invalidationReason: z.string().nullish(),
  treatment: RiskTreatmentEnum,
  residualRisk: RiskRatingEnum.nullable(),
  residualScore: z.number().int().min(0).max(100).nullable().optional(),
  residualLikelihood: RiskLikelihoodEnum.nullable(),
  residualImpact: RiskImpactEnum.nullable(),
  evidenceLinks: z.array(z.string()),
  trackingReferences: z.array(z.string()),
  linkedTests: z.array(z.object({
    cesId: z.string(),
    displayId: z.string().nullable(),
    title: z.string(),
    status: z.string().nullable(),
    lastTestedAt: z.coerce.date().nullable(),
    updatedAt: z.coerce.date().nullable(),
    dueDate: z.coerce.date().nullable(),
    linkSource: z.string(),
    rationale: z.string().nullable(),
  })).default([]),
  sourceRef: z.string().nullable(),
  acceptance: RiskAcceptanceDTOSchema.nullable().optional(),
  // Source tracking
  provider: z.string(),
  externalId: z.string().nullable(),
  externalUrl: z.string().nullable(),
  repository: z.string().nullable(),
  issueState: z.string().nullable(),
  syncStatus: RiskSyncStatusEnum.nullable(),
  syncWarnings: z.array(z.string()).nullable(),
  lastSyncedAt: z.coerce.date().nullable(),
  // Vulnerability CVE identity + SLA (#996)
  cveIds: z.array(z.string()).default([]),
  advisoryIds: z.array(z.string()).default([]),
  packageName: z.string().nullable(),
  firstDetectedAt: z.coerce.date().nullable(),
  slaDueAt: z.coerce.date().nullable(),
  slaState: SlaStateEnum, // derived server-side per-request
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type RiskItemDTO = z.infer<typeof RiskItemDTOSchema>;

export const RiskItemAssessmentValuesSchema = RiskItemDTOSchema.pick({
  likelihood: true, impact: true, ownerId: true, treatment: true,
  mitigationPlan: true, targetDate: true, reviewDate: true,
  residualLikelihood: true, residualImpact: true,
});

export const RiskItemAssessmentDraftSchema = RiskItemAssessmentValuesSchema.extend({
  cesIds: z.array(z.string()).max(10).default([]),
  reasoning: z.string().max(20_000).default(''),
  outcomeConfirmed: z.boolean().default(false),
  outcomeNotes: z.string().max(20_000).default(''),
});
export type RiskItemAssessmentDraft = z.infer<typeof RiskItemAssessmentDraftSchema>;

export const RiskAcceptanceRequestSchema = z.object({
  justification: z.string().trim().min(20).max(4000),
  approverId: z.string().min(1),
  expiresAt: z.coerce.date(),
});
export type RiskAcceptanceRequest = z.infer<typeof RiskAcceptanceRequestSchema>;

export const CompleteRiskItemAssessmentSchema = z.object({
  assessmentId: z.string(), revision: z.number().int().min(0),
  replacesAssessmentId: z.string().optional(),
  reassessmentRiskId: z.string().optional(), // Risk identity for a review opened from saved values (Open or In Progress).
  riskUpdatedAt: z.coerce.date(), draft: RiskItemAssessmentDraftSchema,
  createTask: z.boolean(), acceptanceRequest: RiskAcceptanceRequestSchema.nullable(),
  startTreatment: z.boolean().optional(),
  finishTreatment: z.boolean().optional(),
});

export const RiskItemAssessmentSnapshotSchema = z.object({
  risk: RiskItemDTOSchema.extend({ scoringVersion: z.union([z.literal(1), z.literal(2)]).default(1) }),
  draft: RiskItemAssessmentDraftSchema,
  completedByName: z.string().nullable(),
  startedByName: z.string().nullable().optional(),
  submissionHash: z.string().optional(),
  confirmedPlan: z.object({
    draft: RiskItemAssessmentDraftSchema,
    ownerLabel: z.string(),
    confirmedByName: z.string().nullable(),
  }).optional(),
  acceptanceRejection: z.object({
    at: z.coerce.date(), actorUserId: z.string().nullable(), reason: z.string().nullable(),
  }).optional(),
  riskClosedAt: z.coerce.date().optional(),
  recordMarkdown: z.string().optional(),
  recordFile: z.object({ filename: z.string(), url: z.string(), sha256: z.string() }).optional(),
});

export const RiskItemAssessmentDTOSchema = z.object({
  id: z.string(), riskId: z.string().nullable(), step: z.number().int().min(0).max(3),
  revision: z.number().int(), draft: RiskItemAssessmentDraftSchema,
  aiProposal: z.record(z.unknown()).nullable(),
  aiSuggestionsStale: z.boolean().optional(),
  canReviseTreatment: z.boolean().optional(),
  replacesAssessmentId: z.string().optional(),
  aiStartedAt: z.coerce.date().nullable(), aiCompletedAt: z.coerce.date().nullable(),
  aiError: z.string().nullable(), confirmedAt: z.coerce.date().nullable(),
  confirmedByUserId: z.string().nullable(), supersededAt: z.coerce.date().nullable(),
  startedByUserId: z.string(), startedAt: z.coerce.date(),
  completedByUserId: z.string().nullable(), completedAt: z.coerce.date().nullable(),
  snapshot: RiskItemAssessmentSnapshotSchema.nullable(),
});
export type RiskItemAssessmentDTO = z.infer<typeof RiskItemAssessmentDTOSchema>;

export const CreateRiskItemDTOSchema = z.object({
  vendorId: z.string().min(1).optional(),
  riskType: z.enum(['OPERATIONAL', 'FRAUD']).default('OPERATIONAL'),
  title: z.string().min(1).max(500),
  riskStatement: z.string().min(1),
  affectedSystem: z.string().max(200).optional(),
  category: z.string().min(1).max(100).default('Other'),
  sourceType: RiskSourceTypeEnum.optional(),
  likelihood: RiskLikelihoodEnum.optional(),
  impact: RiskImpactEnum.optional(),
  riskRating: RiskRatingEnum.optional(),
  mitigationPlan: z.string().optional(),
  ownerId: z.string().optional(),
  ownerLabel: z.string().max(200).optional(),
  targetDate: z.coerce.date().optional(),
  reviewDate: z.coerce.date().optional(),
  status: RiskStatusEnum.optional(),
  treatment: RiskTreatmentEnum.optional(),
  residualRisk: RiskRatingEnum.optional(),
  residualLikelihood: RiskLikelihoodEnum.optional(),
  residualImpact: RiskImpactEnum.optional(),
  evidenceLinks: z.array(z.string()).optional(),
  trackingReferences: z.array(z.string()).optional(),
});
export type CreateRiskItemDTO = z.infer<typeof CreateRiskItemDTOSchema>;

export const UpdateRiskItemDTOSchema = z.object({
  title: z.string().min(1).max(500).optional(),
  riskStatement: z.string().min(1).optional(),
  affectedSystem: z.string().max(200).nullable().optional(),
  category: z.string().min(1).max(100).optional(),
  sourceType: RiskSourceTypeEnum.optional(),
  likelihood: RiskLikelihoodEnum.optional(),
  impact: RiskImpactEnum.optional(),
  riskRating: RiskRatingEnum.optional(),
  mitigationPlan: z.string().nullable().optional(),
  ownerId: z.string().nullable().optional(),
  ownerLabel: z.string().max(200).optional(),
  targetDate: z.coerce.date().nullable().optional(),
  reviewDate: z.coerce.date().nullable().optional(),
  status: RiskStatusEnum.exclude(['ASSESSING', 'PENDING_REVIEW', 'CLOSED']).optional(),
  treatment: RiskTreatmentEnum.optional(),
  residualRisk: RiskRatingEnum.nullable().optional(),
  residualLikelihood: RiskLikelihoodEnum.nullable().optional(),
  residualImpact: RiskImpactEnum.nullable().optional(),
  evidenceLinks: z.array(z.string()).optional(),
  trackingReferences: z.array(z.string()).optional(),
});
export type UpdateRiskItemDTO = z.infer<typeof UpdateRiskItemDTOSchema>;

export const RiskListFilterSchema = z.object({
  attention: RiskAttentionEnum.optional(),
  validity: z.enum(['valid', 'invalid', 'all']).optional(),
  /** Omit for all owners; null selects risks without an assigned role. */
  ownerId: z.string().min(1).nullable().optional(),
  riskType: RiskTypeEnum.optional(),
  riskRating: RiskRatingEnum.optional(),
  status: RiskStatusEnum.optional(),
  treatment: RiskTreatmentEnum.optional(),
  sortField: RiskSortFieldEnum.optional(),
  sortDirection: RiskSortDirectionEnum.optional(),
  sourceTypes: z.array(RiskSourceTypeEnum).optional(),
  sourceScanRunId: z.string().optional(),
  /** Risks linked to this vendor via VendorRiskLink (vendor detail Risks tab). */
  vendorId: z.string().optional(),
  // #996: filter vulnerabilities by derived SLA state. Translated to a
  // slaDueAt + status WHERE clause at query time (so paging stays correct).
  slaState: SlaStateEnum.optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(100).optional(),
});

export const RiskListResultDTOSchema = z.object({
  items: z.array(RiskItemDTOSchema),
  total: z.number(),
});
export type RiskListResultDTO = z.infer<typeof RiskListResultDTOSchema>;

export const RiskMatrixCountDTOSchema = z.object({
  rating: RiskRatingEnum.optional(),
  likelihood: RiskLikelihoodEnum,
  impact: RiskImpactEnum,
  count: z.number(),
});
export type RiskMatrixCountDTO = z.infer<typeof RiskMatrixCountDTOSchema>;

export const RiskStatsDTOSchema = z.object({
  openSlaBreached: z.number(),
  openSlaDueSoon: z.number(),
  openSlaWithin: z.number(),
  overdue: z.number(),
  reviewDue: z.number(),
  total: z.number(),
  criticalHigh: z.number(),
  open: z.number(),
  mitigated: z.number(),
  pending: z.number(),
  assessing: z.number(),
  pendingReview: z.number(),
  inProgress: z.number(),
  closed: z.number(),
  monitoring: z.number(),
  medium: z.number(),
  low: z.number(),
});
export type RiskStatsDTO = z.infer<typeof RiskStatsDTOSchema>;

// Historical provider IDs for reading old sync results and rejecting stale clients.
export const RiskSyncProviderSchema = z.enum([
  'GitHub', 'GITHUB_SECURITY_ALERTS', 'Aikido', 'Snyk', 'AWS_SECURITY_HUB',
  'AZURE_DEFENDER', 'GCP_SECURITY_COMMAND_CENTER', 'CrowdStrike', 'Wiz', 'Orca',
]);
export type RiskSyncProvider = z.infer<typeof RiskSyncProviderSchema>;
export const RiskSyncScopeSchema = z.object({ provider: RiskSyncProviderSchema.optional() }).optional();

export const SyncConfigDTOSchema = z.object({
  repos: z.array(z.string()),
  lastSyncedAt: z.coerce.date().nullable(),
  jobState: z.string().nullable().optional(),
  lastSyncResult: z.object({
    created: z.number(),
    updated: z.number(),
    stale: z.number(),
    warnings: z.number(),
    total: z.number(),
    repoErrors: z.array(z.object({ repo: z.string(), message: z.string(), optional: z.boolean().optional() })).optional(),
  }).nullable(),
});
export type SyncConfigDTO = z.infer<typeof SyncConfigDTOSchema>;

export const RiskAssessmentRegisterPageDTOSchema = z.object({
  snapshotId: z.string(), capturedAt: z.string(), digest: z.string(),
  total: z.number(), current: z.number(), closed: z.number(),
  rows: z.array(z.object({
    id: z.string(), code: z.string(), title: z.string(), rating: z.string(),
    owner: z.string(), treatment: z.string(), status: z.string(), targetDate: z.string().nullable(),
  })),
});
export type RiskAssessmentRegisterPageDTO = z.infer<typeof RiskAssessmentRegisterPageDTOSchema>;
